<script setup lang="ts">
import {
  inject,
  onBeforeMount,
  onMounted,
  ref,
  toRef,
  toValue,
  useTemplateRef,
  computed,
} from "vue";
import { useRoute, useRouter } from "vue-router";
import BaseText from "@/library/BaseText.vue";
import SubscriptionService from "@/api/settings/subscription-services";
import CheckoutCardPlans from "@/features/checkout/CheckoutCardPlans.vue";
import CheckoutCardDiscount from "@/features/checkout/CheckoutCardDiscount.vue";
import CheckoutPromoBanner from "@/features/checkout/CheckoutPromoBanner.vue";
import CheckoutCardAccount from "@/features/checkout/CheckoutCardAccount.vue";
import CheckoutCardPayment from "@/features/checkout/CheckoutCardPayment.vue";
import CheckoutCardGuarantee from "@/features/checkout/CheckoutCardGuarantee.vue";
import CheckoutCardFeatures from "@/features/checkout/CheckoutCardFeatures.vue";
import CheckoutHeader from "@/features/checkout/CheckoutHeader.vue";
import PageCheckoutReviews from "@/features/subscribe/PageCheckoutReviews.vue";
import PageCheckoutTrustBadgesBanner from "@/features/subscribe/PageCheckoutTrustBadgesBanner.vue";
import { useColorScheme } from "@/composables/useColorScheme.ts";
import { useSelectedPlan } from "@/features/checkout/useSelectedPlan.ts";
import { useSubscription } from "@/features/checkout/useSubscription.ts";
import {
  accountRecoveryInjectionKey,
  checkoutSessionInjectionKey,
  couponDiscountInjectionKey,
  headlessAuthInjectionKey,
  paypalButtonsInjectionKey,
  stripeElementsInjectionKey,
} from "@/features/checkout/injectionKeys.ts";
import { posthogCapture } from "@/scripts/posthog";
import type { Plan } from "@/features/subscribe/types.ts";
import { usePostHogFeatureFlag } from "@/composables/usePostHogFeatureFlag.js";
import {
  PH_FEATURE_FLAG_CHECKOUT_TRUST_BADGES,
  PH_FEATURE_FLAG_CHECKOUT_REDUCED_VISUAL_CLUTTER,
} from "@/scripts/posthogEvents";

type PageCheckoutCombinedProps = {
  plans: Plan[];
};

type CheckoutStepHandler = () => Promise<boolean>;

/* Feature Flags */
const {
  featureFlag: checkoutTrustBadges,
  hasLoadedFeatureFlag: checkoutTrustBadgesLoaded,
} = usePostHogFeatureFlag(PH_FEATURE_FLAG_CHECKOUT_TRUST_BADGES);

const {
  featureFlag: reducedVisualClutterFlag,
  hasLoadedFeatureFlag: hasLoadedReducedVisualClutterFlag,
} = usePostHogFeatureFlag(PH_FEATURE_FLAG_CHECKOUT_REDUCED_VISUAL_CLUTTER);

const isReducedVisualClutterFlag = computed(
  () => reducedVisualClutterFlag.value === "less-visual-clutter"
);

const showCheckoutTrustBadgesBanner = computed(
  () => checkoutTrustBadges.value === "trust-badges"
);

const props = withDefaults(defineProps<PageCheckoutCombinedProps>(), {
  plans: () => [],
});

const { setColorScheme } = useColorScheme();
onBeforeMount(() => setColorScheme("dark"));

onMounted(() => posthogCapture("user_viewed_checkout_page"));

const router = useRouter();
const route = useRoute();

const cardAccount = useTemplateRef("cardAccount");
const cardPayment = useTemplateRef("cardPayment");

const stripeElements = inject(stripeElementsInjectionKey);
const paypalButtons = inject(paypalButtonsInjectionKey);
const headlessAuth = inject(headlessAuthInjectionKey);
const checkoutSession = inject(checkoutSessionInjectionKey);
const accountRecovery = inject(accountRecoveryInjectionKey);
const couponDiscount = inject(couponDiscountInjectionKey);

const plans = toRef(() => props.plans);
const selectedPlan = useSelectedPlan(plans, checkoutSession);
const subscription = useSubscription();

const createAccountError = ref<string | null>(null);
const processPaymentError = ref<string | null>(null);
const logInError = ref<string | null>(null);

const canShowLoginLink = computed(
  () => !checkoutSession?.status && !checkoutSession?.isPaid
);

const onCreateAccount: CheckoutStepHandler = async () => {
  if (!checkoutSession || !headlessAuth || !cardAccount.value) {
    return false;
  }

  if (checkoutSession.isRegistered) {
    return true;
  }

  checkoutSession.status = "signing-up";

  const credentials = await cardAccount.value.verifyForm();

  if (!credentials) {
    return false;
  }

  await headlessAuth.updateHeadlessUser(credentials);

  if (headlessAuth.updateHeadlessUserError.value) {
    return false;
  }

  await headlessAuth.fetchHeadlessUser();

  return checkoutSession.isRegistered;
};

const onProcessStripe: CheckoutStepHandler = async () => {
  if (!checkoutSession || !stripeElements || !cardPayment.value) {
    return false;
  }

  if (checkoutSession.isPaid) {
    return true;
  }

  checkoutSession.status = "processing-payment";

  const paymentMethodId = await stripeElements.confirmPaymentElement();

  if (stripeElements.stripeError.value || !paymentMethodId) {
    return false;
  }

  try {
    await SubscriptionService.subscribe({
      price_id: toValue(selectedPlan)?.price_id,
      promo_code: toValue(couponDiscount?.coupon) ?? undefined,
      payment_method_id: paymentMethodId,
    });
  } catch {
    await cardPayment.value.refreshStripe();
    return false;
  }

  await subscription.pollSubscription("PAID");

  return checkoutSession.isPaid;
};

const onProcessPaypal: CheckoutStepHandler = async () => {
  if (!checkoutSession || !paypalButtons) {
    return false;
  }

  if (checkoutSession.isPaid) {
    return true;
  }

  checkoutSession.status = "processing-payment";

  if (paypalButtons.paypalError.value) {
    return false;
  }

  try {
    await SubscriptionService.registerPaypal(
      paypalButtons.paypalSubscriptionId.value
    );
  } catch {
    return false;
  }

  await subscription.pollSubscription("PAID");

  return checkoutSession.isPaid;
};

const onLogIn: CheckoutStepHandler = async () => {
  if (!checkoutSession || !headlessAuth) {
    return false;
  }

  if (checkoutSession.isLoggedIn) {
    return true;
  }

  checkoutSession.status = "signing-in";

  const credentials = await headlessAuth.loginHeadlessUser();

  if (headlessAuth.loginUserError.value) {
    return false;
  }

  if (accountRecovery && credentials) {
    if ("phone" in credentials) {
      accountRecovery.value = {
        username: credentials.phone,
        password: null,
        recovery: null,
      };
    } else {
      const recovery = await headlessAuth.encryptHeadlessUser();

      if (headlessAuth.encryptHeadlessUserError.value) {
        return false;
      }

      accountRecovery.value = {
        username: credentials.username,
        password: credentials.password,
        recovery,
      };
    }
  }

  return checkoutSession.isLoggedIn;
};

const onCompleteStripe = async () => {
  if (!checkoutSession) {
    return;
  }

  createAccountError.value = null;
  processPaymentError.value = null;
  logInError.value = null;

  const isRegistered = await onCreateAccount();

  if (!isRegistered) {
    checkoutSession.status = null;
    createAccountError.value = "Failed to create an account. Please try again.";
    return;
  }

  const isLoggedIn = await onLogIn();

  if (!isLoggedIn) {
    logInError.value = "Failed to log in. Please try again.";
    checkoutSession.status = null;
    return;
  }

  const isPaid = await onProcessStripe();

  if (!isPaid) {
    checkoutSession.status = null;
    processPaymentError.value = "Failed to process payment. Please try again.";
    return;
  }

  await router.push({
    name: "CheckoutSuccess",
    query: route.query,
  });

  checkoutSession.status = null;
};

const onCompletePaypal = async () => {
  if (!checkoutSession) {
    return;
  }

  createAccountError.value = null;
  processPaymentError.value = null;
  logInError.value = null;

  if (!checkoutSession.isRegistered) {
    const isRegistered = await onCreateAccount();

    if (!isRegistered) {
      checkoutSession.status = null;
      createAccountError.value =
        "Failed to create an account. Please try again.";
      return;
    }
  }

  if (!checkoutSession.isLoggedIn) {
    const isLoggedIn = await onLogIn();

    if (!isLoggedIn) {
      logInError.value = "Failed to log in. Please try again.";
      checkoutSession.status = null;
      return;
    }

    checkoutSession.status = null;
    return;
  }

  const isPaid = await onProcessPaypal();

  if (!isPaid) {
    checkoutSession.status = null;
    processPaymentError.value = "Failed to process payment. Please try again.";
    return;
  }

  await router.push({
    name: "CheckoutSuccess",
    query: route.query,
  });

  checkoutSession.status = null;
};

const onComplete = () => {
  if (!checkoutSession) {
    return;
  }

  return checkoutSession.method === "stripe"
    ? onCompleteStripe()
    : onCompletePaypal();
};
</script>

<template>
  <div class="page-checkout-combined">
    <template v-if="checkoutTrustBadgesLoaded">
      <PageCheckoutTrustBadgesBanner v-if="showCheckoutTrustBadgesBanner" />
      <PageCheckoutReviews v-else />
    </template>
    <CheckoutHeader
      v-if="!isReducedVisualClutterFlag && hasLoadedReducedVisualClutterFlag"
    >
      <RouterLink
        v-if="canShowLoginLink"
        to="/auth/login"
        class="page-checkout-combined__login"
      >
        <BaseText variant="body-3-semibold">Already Subscribed?</BaseText>
      </RouterLink>
    </CheckoutHeader>
    <div class="page-checkout-combined__content">
      <CheckoutPromoBanner class="page-checkout-combined__promo-banner" />
      <CheckoutCardPlans
        :plans="plans"
        :hide-title="
          isReducedVisualClutterFlag || !hasLoadedReducedVisualClutterFlag
        "
        class="page-checkout-combined__plans"
      />
      <CheckoutCardGuarantee class="page-checkout-combined__guarantee" />
      <CheckoutCardDiscount class="page-checkout-combined__discount" />
      <div class="page-checkout-combined__main">
        <CheckoutCardAccount
          ref="cardAccount"
          :error="createAccountError || logInError"
          class="page-checkout-combined__account"
        >
          <template #header-right>
            <RouterLink
              v-if="
                canShowLoginLink &&
                isReducedVisualClutterFlag &&
                hasLoadedReducedVisualClutterFlag
              "
              to="/auth/login"
              class="page-checkout-combined__login page-checkout-combined__login--underline"
            >
              <BaseText variant="body-3-semibold">Sign in</BaseText>
            </RouterLink>
          </template>
        </CheckoutCardAccount>
        <CheckoutCardPayment
          ref="cardPayment"
          :plans="plans"
          :error="processPaymentError"
          class="page-checkout-combined__payment"
          @complete="onComplete"
        />
      </div>
      <CheckoutCardFeatures class="page-checkout-combined__features" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.page-checkout-combined {
  @include cloaked-gradient-background;

  &__login {
    opacity: 0.8;

    &--underline {
      text-decoration: underline;

      &:hover {
        opacity: 0.6;
      }
    }

    &:hover {
      text-decoration: underline;
    }
  }

  &__content {
    max-width: 946px + 2 * 16px;
    width: 100%;
    padding: 0 16px 16px;
    margin: 12px auto 0;
    display: grid;
    gap: 16px;
    grid-template-areas: "plans" "guarantee" "main" "features";
    grid-template-rows: min-content min-content min-content min-content;

    @media all and (min-width: $screen-md) {
      grid-template-columns: 1fr 1fr;
      grid-template-areas: "plans main" "guarantee main" "features main" "empty main";
      padding: 0 16px 32px;
      margin-top: 32px;
      gap: 24px;
      column-gap: 48px;
    }

    @media all and (min-width: $screen-sm) {
      margin: 24px auto 0;
    }

    &:has(.page-checkout-combined__promo-banner) {
      grid-template-areas: "promo-banner" "plans" "guarantee" "main" "features";
      grid-template-rows: min-content min-content min-content min-content min-content;

      @media all and (min-width: $screen-md) {
        grid-template-areas: "promo-banner main" "plans main" "guarantee main" "features main" "empty main";
      }
    }

    &:has(.page-checkout-combined__discount) {
      grid-template-areas: "plans" "guarantee" "discount" "main" "features";
      grid-template-rows: min-content min-content min-content min-content min-content;

      @media all and (min-width: $screen-md) {
        grid-template-areas: "plans main" "guarantee main" "discount main" "features main" "empty main";
      }
    }

    &:has(.page-checkout-combined__promo-banner):has(
        .page-checkout-combined__discount
      ) {
      grid-template-areas: "promo-banner" "plans" "guarantee" "discount" "main" "features";
      grid-template-rows: min-content min-content min-content min-content min-content min-content;

      @media all and (min-width: $screen-md) {
        grid-template-areas: "promo-banner main" "plans main" "guarantee main" "discount main" "features main" "empty main";
      }
    }
  }

  &__main {
    grid-area: main;
    display: grid;
    align-items: start;
    grid-template-rows: min-content min-content;
    gap: 16px;

    @media all and (min-width: $screen-md) {
      gap: 24px;
    }
  }

  &__promo-banner {
    grid-area: promo-banner;
  }

  &__plans {
    grid-area: plans;
  }

  &__guarantee {
    grid-area: guarantee;
  }

  &__discount {
    grid-area: discount;
  }

  &__features {
    grid-area: features;
  }

  @at-root .theme-dark & {
    :deep(.base-input__title),
    :deep(.base-select__title) {
      color: $color-base-black-70;
    }

    :deep(.base-input__action) {
      color: $color-base-black-100;
    }

    :deep(.base-input:has(.base-input__input:disabled) .base-input__action),
    :deep(
      .base-select:has(.base-select__select:disabled) .base-select__chevron
    ) {
      color: $color-base-black-20;
    }

    :deep(.base-input__input),
    :deep(.base-select__select) {
      background-color: $color-base-black-5;
      border: 1px solid $color-base-black-5;
      color: $color-base-black-100;
      opacity: 1;

      &::placeholder {
        color: $color-base-black-20;
      }

      &:hover {
        border: 1px solid $color-base-black-10;
      }

      &:active,
      &:focus {
        border: 1px solid $color-base-black-70;
      }

      &:disabled {
        background-color: $color-base-black-5;
        border: 1px solid $color-base-black-5;
        color: $color-base-black-20;

        &::placeholder {
          color: $color-base-black-20;
        }
      }
    }

    // for windows selects
    :deep(.base-select__option) {
      background-color: $color-base-white-90;
      color: $color-base-black-90;
    }

    :deep(.base-input__input--error),
    :deep(.base-input__select--error) {
      border-color: $color-alert;

      &:hover,
      &:active,
      &:focus {
        border-color: $color-alert;
      }
    }
  }
}
</style>
